const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

async function request(path: string, options: RequestInit = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> || {}),
  };
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.status === 204 ? null : res.json();
}

export const api = {
  auth: {
    register: (data: any) => request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
    login: (data: any) => request('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
    me: () => request('/auth/me'),
  },
  books: {
    list: (params?: string) => request(`/books${params || ''}`),
    get: (slug: string) => request(`/books/${slug}`),
  },
  progress: {
    update: (bookId: string, data: any) => request(`/progress/${bookId}`, { method: 'POST', body: JSON.stringify(data) }),
    list: () => request('/progress'),
  },
  subscriptions: {
    create: (data: any) => request('/subscriptions', { method: 'POST', body: JSON.stringify(data) }),
    get: () => request('/subscriptions'),
  },
  payments: {
    create: (endpoint: string, data: { plan_type: string; provider: string }) =>
      request(endpoint, { method: 'POST', body: JSON.stringify(data) }),
  },
  health: () => request('/health'),
  ai: {
    generate: (data: any) => request('/ai/generate', { method: 'POST', body: JSON.stringify(data) }),
    status: (sessionId: string) => request(`/ai/generate/${sessionId}`),
    continue: (data: any) => request('/ai/continue', { method: 'POST', body: JSON.stringify(data) }),
    stream: (sessionId: string): EventSource => {
      const base = API_BASE;
      return new EventSource(`${base}/ai/stream?session_id=${sessionId}`);
    },
    history: (limit?: number, offset?: number) =>
      request(`/ai/sessions?limit=${limit ?? 20}&offset=${offset ?? 0}`),
    deleteSession: (id: string) => request(`/ai/sessions/${id}`, { method: 'DELETE' }),
    publish: (sessionId: string, data?: any) =>
      request(`/ai/publish/${sessionId}`, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  },
  share: {
    create: (data: any) => request('/share', { method: 'POST', body: JSON.stringify(data) }),
    get: (token: string) => request(`/share/${token}`),
    view: (token: string) => request(`/share/${token}/view`, { method: 'POST' }),
  },
  reviews: {
    list: (bookId: string) => request(`/reviews/books/${bookId}`),
    create: (data: any) => request('/reviews', { method: 'POST', body: JSON.stringify(data) }),
  },
  favorites: {
    list: () => request('/favorites'),
    toggle: (data: any) => request('/favorites', { method: 'POST', body: JSON.stringify(data) }),
  },
  parent: {
    stats: () => request('/parent/stats'),
  },
  admin: {
    listBooks: (status?: string, limit?: number, offset?: number) =>
      request(`/admin/books${status ? `?status=${status}` : ''}${limit !== undefined ? `${status ? '&' : '?'}limit=${limit}&offset=${offset ?? 0}` : ''}`),
    createBook: (data: any) => request('/admin/books', { method: 'POST', body: JSON.stringify(data) }),
    updateBook: (id: string, data: any) => request(`/admin/books/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteBook: (id: string) => request(`/admin/books/${id}`, { method: 'DELETE' }),
    updateStatus: (bookId: string, status: string) => request(`/admin/books/${bookId}/status?status=${status}`, { method: 'PATCH' }),
    bookPages: (bookId: string) => request(`/admin/books/${bookId}/pages`),
    updatePage: (bookId: string, pageId: string, data: any) =>
      request(`/admin/books/${bookId}/pages/${pageId}`, { method: 'PUT', body: JSON.stringify(data) }),
    stats: () => request('/admin/stats'),
    users: (params?: string) => request(`/admin/users${params || ''}`),
    pendingReviews: () => request('/admin/reviews/pending'),
    approveReview: (id: string) => request(`/admin/reviews/${id}/approve`, { method: 'POST' }),
    rejectReview: (id: string) => request(`/admin/reviews/${id}/reject`, { method: 'POST' }),
    toggleBook: (id: string) => request(`/admin/books/${id}/toggle-publish`, { method: 'POST' }),
  },
  notifications: {
    list: () => request('/notifications'),
    markRead: (id: string) => request(`/notifications/${id}/read`, { method: 'POST' }),
    markAllRead: () => request('/notifications/read-all', { method: 'POST' }),
    clearAll: () => request('/notifications', { method: 'DELETE' }),
  },
  settings: {
    notifications: () => request('/settings/notifications'),
    updateNotifications: (data: any) => request('/settings/notifications', { method: 'POST', body: JSON.stringify(data) }),
  },
  children: {
    list: () => request('/children'),
    create: (data: any) => request('/children', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/children/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => request(`/children/${id}`, { method: 'DELETE' }),
  },
};
